//#region types
import { FormEnums } from "models/enums";
import { RevenueReportingFormTypes } from "models/formTypes";
import { AutoCompleteExtraProps, GenericFormSection } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { FilterQueryStateCoder, FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { useCompanyService, useRevenueReportingService } from "serviceHooks";
//#endregion
import { FilterHelper, LocalizationHelperInstance, QueryStateHelper } from "utils/helpers";

function useRevenueReportListFilterDefinition() {
  const revenueReportingService = useRevenueReportingService();
  const companyService = useCompanyService();

  const statusItems: SelectionItem[] = [
    {
      id: WintSuperkollAPIModel.RevenueReportStatus.Reported,
      value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.RevenueReporting.StatusReported),
    },
    {
      id: WintSuperkollAPIModel.RevenueReportStatus.Voided,
      value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.RevenueReporting.StatusVoided),
    },
  ];

  const filterValueFormatter = (values: RevenueReportingFormTypes.Filter): RevenueReportingFormTypes.Filter => ({
    ...values,
    hideLegacy: values?.hideLegacy !== false,
  });

  const hideLegacyQueryCoder: FilterQueryStateCoder<boolean | undefined> = {
    urlEncode: (params, currentValue, newValue) => {
      if (newValue === false) {
        params.set(FormFieldConstants.RevenueReporting.Filter.HideLegacy, "false");
      } else {
        params.delete(FormFieldConstants.RevenueReporting.Filter.HideLegacy);
      }
    },
    urlDecode: (params) => params.get(FormFieldConstants.RevenueReporting.Filter.HideLegacy) !== "false",
  };

  const apiToReportFilterFormatter = async (filter: WintSuperkollAPIModel.RevenueReportFilter) => {
    const reportFilter: RevenueReportingFormTypes.Filter = {};

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.RevenueReporting.Filter.Date,
      filter.fromDate,
      filter.toDate
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.RevenueReporting.Filter.SaleAmount,
      filter.minAmount,
      filter.maxAmount
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.RevenueReporting.Filter.DifferenceAmount,
      filter.minDifference,
      filter.maxDifference
    );

    if (filter.templateId) {
      reportFilter.templateId = filter.templateId;
    }

    if (filter.status !== undefined) {
      reportFilter.status = filter.status;
    }

    let registeredByName: Promise<string> | undefined;
    if (filter.ownerIds?.length) {
      const ownerId = filter.ownerIds[0];
      reportFilter.registeredById = ownerId;
      registeredByName = companyService.getEmployeeName(ownerId);
    }

    if (registeredByName) {
      reportFilter.registeredBy = await registeredByName;
    }

    return reportFilter;
  };

  const filterFormFields: GenericFormSection<RevenueReportingFormTypes.Filter>[] = [
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        date: {
          label: TranslationKeys.Common.Date,
          type: FormEnums.FormFieldType.DATERANGE,
        },
        registeredBy: {
          label: TranslationKeys.CashReconciliation.RegisteredBy,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          placeholder: TranslationKeys.Common.Search,
          extraProps: {
            referenceFieldName: FormFieldConstants.RevenueReporting.Filter.RegisteredById,
            items: (searchStr: string) => companyService.SearchPersonsAutoComplete(searchStr, false),
          } as AutoCompleteExtraProps<RevenueReportingFormTypes.Filter>,
        },
        template: {
          label: TranslationKeys.RevenueReporting.PaymentMethod,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          placeholder: TranslationKeys.Common.Search,
          extraProps: {
            referenceFieldName: FormFieldConstants.RevenueReporting.Filter.TemplateId,
            items: async (searchStr: string) => {
              const templates = await revenueReportingService.getTemplates();
              return (templates ?? [])
                .filter((t) => !searchStr || (t.name ?? "").toLowerCase().includes(searchStr.toLowerCase()))
                .map((t) => ({ id: t.id, value: t.name ?? "", title: t.name ?? "" }));
            },
          } as AutoCompleteExtraProps<RevenueReportingFormTypes.Filter>,
        },
        status: {
          label: TranslationKeys.Common.Status,
          type: FormEnums.FormFieldType.SELECTION,
          extraProps: {
            items: statusItems,
          },
        },
        hideLegacy: {
          label: TranslationKeys.RevenueReporting.HideLegacyReports,
          type: FormEnums.FormFieldType.TOGGLE,
          checkHasValue: (value: boolean | undefined) => value === false,
        },
      },
    },
    {
      label: TranslationKeys.Common.Amount,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        differenceAmount: {
          label: TranslationKeys.RevenueReporting.Difference,
          type: FormEnums.FormFieldType.NUMBERRANGE,
          extraProps: {
            allowNegative: true,
            decimalPoints: 2,
          },
        },
        saleAmount: {
          label: TranslationKeys.CashReconciliation.Sale,
          type: FormEnums.FormFieldType.NUMBERRANGE,
          extraProps: {
            allowNegative: true,
            decimalPoints: 2,
          },
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<RevenueReportingFormTypes.Filter> = {
    date: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.RevenueReporting.Filter.Date),
    registeredBy: QueryStateHelper.stringQueryEncoder(FormFieldConstants.RevenueReporting.Filter.RegisteredBy),
    registeredById: QueryStateHelper.numberQueryEncoder(FormFieldConstants.RevenueReporting.Filter.RegisteredById),
    template: QueryStateHelper.stringQueryEncoder(FormFieldConstants.RevenueReporting.Filter.Template),
    templateId: QueryStateHelper.stringQueryEncoder(FormFieldConstants.RevenueReporting.Filter.TemplateId),
    status: QueryStateHelper.numberQueryEncoder(FormFieldConstants.RevenueReporting.Filter.Status),
    saleAmount: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.RevenueReporting.Filter.SaleAmount),
    differenceAmount: QueryStateHelper.numberRangeQueryEncoder(
      FormFieldConstants.RevenueReporting.Filter.DifferenceAmount
    ),
    hideLegacy: hideLegacyQueryCoder,
  };

  return { filterFormFields, filterValueFormatter, filterQueryStateHandler, apiToReportFilterFormatter };
}

export default useRevenueReportListFilterDefinition;
